// Co-occurrence arch for the X-ray Terms panel — the relational layer over
// termRank's ranked terms. A classic chord diagram bent to the panel's
// wide-strip geometry: terms run ALPHABETICALLY as segments along a
// half-ellipse (span ∝ prompt count), ribbons through the dome connect
// co-occurring terms (width ∝ shared prompts, allocated as true sub-spans
// of each segment). Everything here is pure ANGLE-space math —
// deterministic, DOM-free, unit-testable; the panel component maps angles
// to ellipse pixels.

import { termsOf } from "./termRank.js";

/**
 * Pairs of kept terms sharing a prompt, weighted by shared-prompt count.
 * Pairs where one term word-contains the other (a bigram vs its own parts)
 * are degenerate — they co-occur by construction — and are skipped. Edges
 * below the document-frequency floor drop (relaxed to 1 for small windows,
 * mirroring termRank). The per-node degree cap admits an edge if EITHER
 * endpoint still has room, so a leaf term keeps its strongest link even
 * when its hub partner is saturated.
 */
export function cooccurrence(summaries, keptTerms, { maxNeighbors = 4 } = {}) {
    const kept = new Set(keptTerms);
    const wordContains = (a, b) => ` ${a} `.includes(` ${b} `) || ` ${b} `.includes(` ${a} `);
    const pair = new Map();
    for (const s of summaries || []) {
        const ts = [...termsOf(s)].filter((t) => kept.has(t)).sort();
        for (let i = 0; i < ts.length; i++) {
            for (let j = i + 1; j < ts.length; j++) {
                if (wordContains(ts[i], ts[j])) continue;
                const k = `${ts[i]}\0${ts[j]}`;
                pair.set(k, (pair.get(k) || 0) + 1);
            }
        }
    }
    const floor = (summaries?.length || 0) >= 40 ? 2 : 1;
    const ranked = [...pair.entries()]
        .map(([k, weight]) => {
            const [a, b] = k.split("\0");
            return { a, b, weight };
        })
        .filter((e) => e.weight >= floor)
        .sort((x, y) => (y.weight - x.weight) || x.a.localeCompare(y.a) || x.b.localeCompare(y.b));
    const degree = new Map();
    const edges = [];
    for (const e of ranked) {
        const da = degree.get(e.a) || 0;
        const db = degree.get(e.b) || 0;
        if (da >= maxNeighbors && db >= maxNeighbors) continue;
        edges.push(e);
        degree.set(e.a, da + 1);
        degree.set(e.b, db + 1);
    }
    return edges;
}

/**
 * Pixel-space frame for the arch: how the abstract [π, 0] order parameter
 * maps onto an ellipse arc in a given panel. Wide panels squash the dome
 * (ry < rx); tall panels must never STRETCH it — a prolate dome reads as a
 * tunnel, flanks vertical and all curvature crammed at the apex. So the
 * dome caps at a half-circle (ry ≤ rx) and extra height is consumed by
 * sweeping the arc PAST the equator by `psi` on each side — a horseshoe
 * closing toward a full chord circle — with any remaining height split
 * into symmetric margins. `maxCloseness` (sin ψ) keeps a bottom opening
 * so the ends never quite meet.
 *
 * @returns {{ cx, cy, rx, ry, psi }} — ellipse center, radii, over-sweep
 */
export function archFrame(width, height, {
    padX = 150, padTop = 132, padBottom = 26, minRx = 180, minRy = 70, maxCloseness = 0.85,
} = {}) {
    const cx = width / 2;
    const rx = Math.max(minRx, width / 2 - padX);
    const avail = height - padTop - padBottom;
    if (avail <= rx) {
        return { cx, cy: height - padBottom, rx, ry: Math.max(minRy, avail), psi: 0 };
    }
    const ry = rx;
    const sinPsi = Math.min(maxCloseness, avail / ry - 1);
    const psi = Math.asin(sinPsi);
    const leftover = avail - ry * (1 + sinPsi);
    return { cx, cy: padTop + leftover / 2 + ry, rx, ry, psi };
}

/**
 * Angular layout of the arch over θ ∈ [π, 0] (left end → right end).
 * Each term gets a segment whose span is proportional to its count;
 * `gapFraction` of the half-circle is reserved for inter-segment gaps.
 * Each ribbon endpoint gets a SUB-span of its segment, allocated among the
 * segment's ribbons proportionally to weight and ordered by the partner's
 * position — the chord-diagram treatment, so a hub's span visibly divides
 * among its relationships.
 *
 * @returns {{ order: string[], segments: Map<term,{a,b}>,
 *             ribbons: {a,b,weight,spanA:[θ1,θ2],spanB:[θ1,θ2]}[], maxWeight }}
 */
export function buildArch(terms, edges, { gapFraction = 0.22 } = {}) {
    if (!terms.length) return { order: [], segments: new Map(), ribbons: [], maxWeight: 1 };
    const kept = new Set(terms.map((t) => t.term));
    const usableEdges = edges.filter((e) => kept.has(e.a) && kept.has(e.b));
    // Alphabetical around the arch, always. Ordering by graph affinity put
    // related terms side by side but was chaotically input-sensitive — one
    // prompt entering the window reshuffled the whole arch, so the panel
    // never rendered the same way twice. A fixed collation keeps each term
    // in the same neighborhood across refreshes (and makes it findable by
    // eye); relatedness stays visible through the ribbons themselves.
    const order = terms.map((t) => t.term).sort((a, b) => a.localeCompare(b));
    const countOf = new Map(terms.map((t) => [t.term, t.count]));

    const total = order.reduce((s, t) => s + countOf.get(t), 0) || 1;
    const gap = (Math.PI * gapFraction) / order.length;
    const usable = Math.PI * (1 - gapFraction);
    const segments = new Map();
    let cur = Math.PI;
    for (const term of order) {
        const span = usable * (countOf.get(term) / total);
        segments.set(term, { a: cur, b: cur - span });
        cur = cur - span - gap;
    }

    const incident = new Map(order.map((t) => [t, []]));
    for (const e of usableEdges) {
        incident.get(e.a).push(e);
        incident.get(e.b).push(e);
    }
    const anchors = new Map();
    const mid = (t) => {
        const s = segments.get(t);
        return (s.a + s.b) / 2;
    };
    for (const term of order) {
        const es = incident.get(term);
        if (!es.length) continue;
        const { a, b } = segments.get(term);
        es.sort((x, y) => mid(x.a === term ? x.b : x.a) - mid(y.a === term ? y.b : y.a));
        const wsum = es.reduce((s, e) => s + e.weight, 0);
        let c = a;
        for (const e of es) {
            const sp = (a - b) * (e.weight / wsum);
            anchors.set(`${e.a}\0${e.b}\0${term}`, [c, c - sp]);
            c -= sp;
        }
    }
    const ribbons = usableEdges.map((e) => ({
        ...e,
        spanA: anchors.get(`${e.a}\0${e.b}\0${e.a}`),
        spanB: anchors.get(`${e.a}\0${e.b}\0${e.b}`),
    }));
    const maxWeight = ribbons.reduce((m, r) => Math.max(m, r.weight), 1);
    return { order, segments, ribbons, maxWeight };
}

// ── ribbon geometry ──────────────────────────────────────────────────────────
//
// The naive ribbon path (M a1 L a2 Q ctrl b1 L b2 Q ctrl a1) draws its anchor
// edges as straight CHORDS between the endpoints. Invisible when the arch has
// ~40 sliver segments, but a sparse arch hands one ribbon an enormous angular
// span and the chord cuts straight across the dome as a giant wedge that
// follows neither arc. The fix: anchor edges are SAMPLED along the ellipse
// exactly like the segments themselves, spanning the anchor edge-to-edge so
// the fill meets its whole segment (clamping the anchors was tried and read
// as ribbons detaching from their segments).

/**
 * Closed ribbon path hugging the dome: arc along A's anchor, bezier to B,
 * arc along B's anchor, bezier home. P maps an angle parameter to [x, y]
 * (the caller's ellipse); ctrl is the shared bezier control point.
 */
export function archRibbonPath(P, spanA, spanB, ctrl, { samples = 12 } = {}) {
    const [a1, a2] = spanA;
    const [b1, b2] = spanB;
    const pt = (t) => {
        const [x, y] = P(t);
        return `${x.toFixed(1)} ${y.toFixed(1)}`;
    };
    const arc = (from, to) => {
        let d = "";
        for (let i = 1; i <= samples; i++) d += `L ${pt(from + ((to - from) * i) / samples)} `;
        return d;
    };
    const c = `${ctrl[0].toFixed(1)} ${ctrl[1].toFixed(1)}`;
    return `M ${pt(a1)} ${arc(a1, a2)}Q ${c} ${pt(b1)} ${arc(b1, b2)}Q ${c} ${pt(a1)} Z`;
}
